import { DelegatedLink } from "@/components/shared/delegated-link";
import { usePostContext } from "@/contexts/post-context";
import { cn } from "@/utils/lib";
import type { Maybe } from "@/utils/types";

import DesoAvatar from "../../shared/deso-avatar";
import { PostCompactActivity } from "../post/post-activity/post-compact-activity";
import { PostDetailsView } from "../post/post-details-header";
import { PostHeaderBody } from "../post/post-header-body";
import { PostStatusRow } from "../post/post-status-row";
import { PostUpvote } from "../post/post-upvote";

type FeedPostDetailsProps = {
  className?: Maybe<string>;
  showRepost?: boolean;
  clickable?: boolean;
};

export const FeedPostDetails = ({
  className = "",
  showRepost = true,
  clickable = false,
}: FeedPostDetailsProps) => {
  const {
    publicKey,
    repost,
    isPureRepost,
    poster,
    isPostRemoved,
    reactionsCount,
    repostReactionsCount,
    post,
    hideAvatar,
    hideUpvote,
    hideActivity,
  } = usePostContext();

  const creatorPublicKey = (isPureRepost ? repost : post)?.poster?.publicKey;
  const postHash = (isPureRepost ? repost : post)?.postHash;

  // If a post is hidden, then don't return anything.
  if (isPostRemoved || !post.postHash || !creatorPublicKey || !postHash) {
    return null;
  }

  return (
    <>
      <div
        className="
          pl-4
          md:pl-20
        "
      >
        <PostStatusRow section="feed" />
      </div>

      <DelegatedLink
        enabled={clickable}
        href={`/post/${postHash}`}
        className={cn(
          `
            flex items-stretch gap-4 p-4
            md:p-6
            xl:p-8
          `,
          { "cursor-pointer": clickable },
          className,
        )}
      >
        {(!hideAvatar || !hideUpvote) && (
          <div
            className="
              hidden items-center justify-start gap-2
              md:flex md:flex-col
            "
          >
            {!hideAvatar && publicKey && poster && (
              <DesoAvatar {...{ account: poster, size: 56 }} />
            )}
            {!hideUpvote && <PostUpvote />}
          </div>
        )}
        {/*Contents with overflow hidden and 100% width (like the code blocks) can overflow,
        we need a fixed size. This width is 100% - Avatar column width - gap */}
        <div
          className="
            flex w-full min-w-0 flex-col gap-1
            md:max-w-[calc(100%-56px-1rem)]
          "
        >
          <PostHeaderBody
            {...{
              showRepost,
              postType: PostDetailsView.feed,
              PostBodyClass: "py-0",
            }}
          />
          {!hideActivity && (
            <PostCompactActivity
              creatorPublicKey={creatorPublicKey}
              postHash={postHash}
              hideEmojiActivity={
                isPureRepost ? repostReactionsCount === 0 : reactionsCount === 0
              }
            />
          )}
        </div>
      </DelegatedLink>
    </>
  );
};
